import React, { useState } from 'react';
import { AllowanceStatus } from '../lib/gameEngine';
import './TopBar.css';

interface TopBarProps {
  wallet: number;
  isGuest?: boolean;
  username?: string;
  allowanceStatus?: AllowanceStatus | null;
  onClaimAllowance?: () => Promise<{ success: boolean; amountClaimed?: number; error?: string }>;
  onOpenSignIn: () => void;
  onSignOut?: () => void;
}

function formatCountdown(totalSecs: number): string {
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export const TopBar: React.FC<TopBarProps> = ({
  wallet,
  isGuest = true,
  username,
  allowanceStatus,
  onClaimAllowance,
  onOpenSignIn,
  onSignOut
}) => {
  const [claiming, setClaiming] = useState<boolean>(false);
  const [justClaimedAmount, setJustClaimedAmount] = useState<number | null>(null);

  const handleClaim = async () => {
    if (!onClaimAllowance || claiming || !allowanceStatus?.isClaimable) return;
    setClaiming(true);
    try {
      const res = await onClaimAllowance();
      if (res.success && res.amountClaimed) {
        setJustClaimedAmount(res.amountClaimed);
        setTimeout(() => setJustClaimedAmount(null), 3000);
      }
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="top-bar-container">
      {/* Left Section: Bankroll Pill + Black Allowance Button */}
      <div className="top-bar-left">
        <div className="top-bar-pill">
          <span className="bankroll-label">Bankroll:</span>
          <span className="bankroll-amount">€{wallet.toFixed(2)}</span>
        </div>

        {allowanceStatus && (
          <button
            className="allowance-btn"
            onClick={handleClaim}
            disabled={!allowanceStatus.isClaimable || claiming}
          >
            {claiming ? (
              'Claiming...'
            ) : justClaimedAmount ? (
              `+€${justClaimedAmount.toFixed(2)}`
            ) : allowanceStatus.isClaimable ? (
              `+€${allowanceStatus.allowanceAmount.toFixed(2)}`
            ) : (
              formatCountdown(allowanceStatus.secondsRemaining)
            )}
          </button>
        )}
      </div>

      {/* Right Pill: User & Sign In / Sign Out */}
      <div className="top-bar-pill user-pill">
        <span className="user-name-label">
          {isGuest ? 'Guest' : (username || 'Player')}
        </span>
        {isGuest ? (
          <button
            className="top-signin-btn"
            onClick={onOpenSignIn}
            title="Sign in with Google or Discord"
          >
            Sign in
          </button>
        ) : (
          <button
            className="top-signin-btn signout"
            onClick={onSignOut}
            title="Sign out of account"
          >
            Sign out
          </button>
        )}
      </div>
    </div>
  );
};
